import { saveIcon } from "assets/images";
import {
  Box,
  Button,
  Card,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { AppPages } from "utils/enums/app-pages";
import {
  EmailPattern,
  getPagePermissions,
  onTextFieldKeyPress,
  onNumberFieldKeyPress,
  showLoader,
} from "utils/helper";
import {
  EditStaffDetailsSuccessPayload,
  StaffDetailsSuccessPayload,
} from "store/student/types";
import { Controller, useForm } from "react-hook-form";
import { IStaffDetails } from "utils/interfaces/student";
import { IEnrolmentOptions } from "utils/interfaces/student-enrolment";
import {
  STAFF_DETAIL_EDIT_SUCCESS_MESSAGE,
  VALIDATION_ERROR_NO_WHITE_SPACE_OR_NUMBER,
} from "utils/constants/Messages";
import { toast } from "react-toastify";
import {
  STUDENT_MANAGER_EMAIL_MAX_LENGTH,
  STUDENT_MANAGER_NAME_MAX_LENGTH,
  STUDENT_ENROLMENT_STAFF_LINK_ID_MAX_LENGTH,
} from "utils/constants/student-enrolment";
import { INPUT_VALIDATION_NO_WHITE_SPACE_AND_NUMBER_WITH_SPECIAL_CHARACTER_REGEX } from "utils/constants/constant";
import { ClearIcon } from "@mui/x-date-pickers";

interface IStaffDetailsProps {
  staffDetailsRequest: any;
  editStaffDetailRequest: any;
  enrolmentOptionList: IEnrolmentOptions | undefined;
}

const StaffDetails: React.FC<IStaffDetailsProps> = ({
  staffDetailsRequest,
  editStaffDetailRequest,
  enrolmentOptionList,
}) => {
  const location = useLocation<{ studentId: number; isDeleted?: boolean }>();
  const history = useHistory();
  const user = useSelector((state: any) => state?.auth?.user);
  const [staffData, setStaffData] = useState<IStaffDetails>();
  const {
    register,
    watch,
    reset,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<IStaffDetails>({
    defaultValues: {
      employmentStatus: "",
      currentRole: "",
      workEnvironment: "",
      site: "",
      employmentNumber: undefined,
    },
  });

  const getManagersEmailError = (): string => {
    if (errors.managerEmail) {
      if (errors.managerEmail.type === "pattern") {
        return "Enter valid email";
      }
      if (errors.managerEmail.type === "maxLength") {
        return `Maximum length of email should be ${STUDENT_MANAGER_EMAIL_MAX_LENGTH}`;
      }
    }

    return "";
  };

  const getManagerNameError = (): string => {
    if (errors.managerName) {
      if (errors.managerName.type === "maxLength") {
        return `Maximum length of name should be ${STUDENT_MANAGER_NAME_MAX_LENGTH}`;
      }
      if (errors.managerName.type === "pattern") {
        return `Manager name ${VALIDATION_ERROR_NO_WHITE_SPACE_OR_NUMBER}`;
      }
    }

    return "";
  };

  const getStaffLinkIdError = (): string => {
    if (errors.employmentNumber) {
      if (errors.employmentNumber.type === "max") {
        return "Max value of Staff Link Id can be '2147483647'";
      }
      if (errors.employmentNumber.type === "maxLength") {
        return `Max value of Staff Link Id can be ${STUDENT_ENROLMENT_STAFF_LINK_ID_MAX_LENGTH}`;
      }
    }

    return "";
  };

  const getEmploymentStatusError = (): string => {
    if (errors.employmentStatus) {
      if (errors.employmentStatus.type === "required") {
        return "Employment Status is required";
      }
    }

    return "";
  };

  const getCurrentRoleError = (): string => {
    if (errors.currentRole) {
      if (errors.currentRole.type === "required") {
        return "Current Role is required";
      }
    }

    return "";
  };

  const getWorkEnvironmentError = (): string => {
    if (errors.workEnvironment) {
      if (errors.workEnvironment.type === "required") {
        return "Work Environment is required";
      }
    }

    return "";
  };

  const getSiteError = (): string => {
    if (errors.site) {
      if (errors.site.type === "required") {
        return "Site/Service is required";
      }
    }

    return "";
  };

  const setStudentFormValues = (researchProfileDetails: any) => {
    reset((formValues) => ({
      ...formValues,
      employmentStatus: researchProfileDetails.employmentStatus,
      currentRole: researchProfileDetails.currentRole,
      workEnvironment: researchProfileDetails.workEnvironment,
      site: researchProfileDetails.site,
      managerName: researchProfileDetails.managerName,
      managerEmail: researchProfileDetails.managerEmail,
      employmentNumber: researchProfileDetails.employmentNumber,
      isActive: researchProfileDetails.isActive,
    }));
  };

  const onResearchProfileDataSuccess = (
    response: StaffDetailsSuccessPayload
  ) => {
    setStudentFormValues(response.StaffDetails);
    setStaffData(response.StaffDetails);
  };

  const getResearchProfileData = () => {
    if (staffDetailsRequest) {
      showLoader();
      const { studentId } = location.state;

      const payload = {
        values: { id: studentId },
        callback: onResearchProfileDataSuccess,
      };

      staffDetailsRequest(payload);
    }
  };

  const handleCancelClick = () => {
    setStudentFormValues(staffData);
  };

  useEffect(() => {
    const permission = getPagePermissions(user?.permissions, AppPages.Student);
    if (location?.state.studentId && permission.edit) {
      getResearchProfileData();
    } else {
      history.goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEditStaffDetailSuccess = (
    response: EditStaffDetailsSuccessPayload
  ) => {
    if (response.success) {
      toast.success(STAFF_DETAIL_EDIT_SUCCESS_MESSAGE);
    } else if (response.message) {
      toast.error(response.message);
    }
  };

  const onSubmit = async (data: IStaffDetails) => {
    if (location.state.studentId && !location.state.isDeleted) {
      if (editStaffDetailRequest) {
        showLoader();
        const payload = {
          values: {
            studentId: location.state.studentId,
            employmentStatus: data.employmentStatus,
            currentRole: data.currentRole,
            workEnvironment: data.workEnvironment,
            site: data.site,
            managerName: data.managerName,
            managerEmail: data.managerEmail,
            employmentNumber: data.employmentNumber || null,
          },
          callback: onEditStaffDetailSuccess,
        };
        editStaffDetailRequest(payload);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card className="std-details-card">
        <Typography variant="h3" className="attnd-heading">
          Staff Details
        </Typography>
        <Grid
          container
          columnSpacing={{
            sm: "16px",
            md: " 20px",
            xl: "24px",
          }}
        >
          <Grid item xs={12} sm={6} lg={4}>
            <FormControl fullWidth className="select">
              <InputLabel
                id="empStatus-label"
                shrink={!!watch("employmentStatus")}
              >
                Employment Status <span className="color-red">*</span>
              </InputLabel>
              <Controller
                name="employmentStatus"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="empStatus-label"
                    id="empStatus"
                    value={field.value}
                    displayEmpty
                    endAdornment={
                      field.value ? (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setValue("employmentStatus", "");
                          }}
                          edge="end"
                          className="clear-btn"
                        >
                          <ClearIcon />
                        </IconButton>
                      ) : null
                    }
                    error={!!errors.employmentStatus}
                  >
                    {enrolmentOptionList?.staffEmploymentStatus?.map(
                      (option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      )
                    )}
                  </Select>
                )}
              />
              {!!errors.employmentStatus && (
                <FormHelperText error>
                  {getEmploymentStatusError()}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <FormControl fullWidth className="select">
              <InputLabel id="currntRole-label" shrink={!!watch("currentRole")}>
                Current Role <span className="color-red">*</span>
              </InputLabel>
              <Controller
                name="currentRole"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="currntRole-label"
                    id="currntRole"
                    value={field.value}
                    displayEmpty
                    endAdornment={
                      field.value ? (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setValue("currentRole", "");
                          }}
                          edge="end"
                          className="clear-btn"
                        >
                          <ClearIcon />
                        </IconButton>
                      ) : null
                    }
                    error={!!errors.currentRole}
                  >
                    {enrolmentOptionList?.currentRole?.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {!!errors.currentRole && (
                <FormHelperText error>{getCurrentRoleError()}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <FormControl fullWidth className="select">
              <InputLabel
                id="workEnv-label"
                shrink={!!watch("workEnvironment")}
              >
                Work Environment <span className="color-red">*</span>
              </InputLabel>
              <Controller
                name="workEnvironment"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="workEnv-label"
                    id="workEnv"
                    value={field.value}
                    displayEmpty
                    endAdornment={
                      field.value ? (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setValue("workEnvironment", "");
                          }}
                          edge="end"
                          className="clear-btn"
                        >
                          <ClearIcon />
                        </IconButton>
                      ) : null
                    }
                    error={!!errors.workEnvironment}
                  >
                    {enrolmentOptionList?.workEnvironment?.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {!!errors.workEnvironment && (
                <FormHelperText error>
                  {getWorkEnvironmentError()}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <FormControl fullWidth className="select">
              <InputLabel id="siteServ-label" shrink={!!watch("site")}>
                Site/Service <span className="color-red">*</span>
              </InputLabel>
              <Controller
                name="site"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="siteServ-label"
                    id="siteServ"
                    value={field.value}
                    displayEmpty
                    endAdornment={
                      field.value ? (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setValue("site", "");
                          }}
                          edge="end"
                          className="clear-btn"
                        >
                          <ClearIcon />
                        </IconButton>
                      ) : null
                    }
                    error={!!errors.site}
                  >
                    {enrolmentOptionList?.siteOrService?.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {!!errors.site && (
                <FormHelperText error>{getSiteError()}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <TextField
              id="mang-name"
              label="Manager's Name"
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: Boolean(watch("managerName")),
              }}
              error={!!errors.managerName}
              onKeyPress={(event) =>
                onTextFieldKeyPress(event, watch("managerName")?.length)
              }
              helperText={getManagerNameError()}
              {...register("managerName", {
                maxLength: STUDENT_MANAGER_NAME_MAX_LENGTH,
                pattern:
                  INPUT_VALIDATION_NO_WHITE_SPACE_AND_NUMBER_WITH_SPECIAL_CHARACTER_REGEX,
              })}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <TextField
              id="mang-email"
              label="Manager's Email"
              fullWidth
              variant="outlined"
              defaultValue="johndoe@gmail.com"
              InputLabelProps={{
                shrink: Boolean(watch("managerEmail")),
              }}
              error={!!errors.managerEmail}
              helperText={getManagersEmailError()}
              {...register("managerEmail", {
                pattern: EmailPattern,
                maxLength: STUDENT_MANAGER_EMAIL_MAX_LENGTH,
              })}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <TextField
              type="number"
              id="staff-link-id"
              label="Staff Link ID"
              fullWidth
              onKeyPress={onNumberFieldKeyPress}
              variant="outlined"
              InputLabelProps={{
                shrink: Boolean(watch("employmentNumber")),
              }}
              error={!!errors.employmentNumber}
              helperText={getStaffLinkIdError()}
              {...register("employmentNumber", {
                maxLength: STUDENT_ENROLMENT_STAFF_LINK_ID_MAX_LENGTH,
                max: 2147483647,
              })}
              value={watch("employmentNumber") || ""}
            />
          </Grid>
        </Grid>
        <Box className="btn-group">
          <Button
            disabled={location.state.isDeleted}
            variant="contained"
            className="btn-save"
            type="submit"
          >
            <img src={saveIcon} alt="save" />
            Save
          </Button>
          <Button
            variant="outlined"
            className="btn-cancel"
            onClick={handleCancelClick}
          >
            Cancel
          </Button>
        </Box>
      </Card>
    </form>
  );
};

export default StaffDetails;
